import React from 'react';
import { Button } from '@/components/ui';
import { ImageWithFallback } from '@/components/ui';
import { STORE_DETAILS, ASSETS, ASSET_DIMENSIONS, FALLBACK_IMAGES } from '@/config/store';

export function HeroSection() {
  return (
    <section className="relative hero-wash min-h-[100svh] flex flex-col pt-16 sm:pt-[4.5rem] overflow-hidden">
      <div className="flex-1 flex items-center mx-auto w-full max-w-6xl px-4 sm:px-6 py-8 sm:py-10 lg:py-12">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 sm:gap-10 lg:gap-16 items-center w-full">
          <div className="max-w-xl relative z-10 order-2 lg:order-1">
            <p className="animate-hero-fade inline-flex items-center gap-2 text-sm font-medium text-brand tracking-wide">
              <span className="h-px w-6 bg-brand/60" aria-hidden />
              Pokhara-1 · Bagbazar · Est. {STORE_DETAILS.established}
            </p>
            <h1 className="animate-hero-fade-delay mt-3 sm:mt-4 font-display text-[2rem] leading-[1.1] sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight text-ink sm:leading-[1.06]">
              Bagbazar
              <span className="block text-brand">Provision Stores</span>
            </h1>
            <p className="animate-hero-fade-delay mt-4 sm:mt-5 text-base sm:text-lg text-muted leading-relaxed max-w-md">
              Daily essentials, grains, and groceries for the neighborhood.
            </p>
            <div className="animate-hero-fade-delay-2 mt-6 sm:mt-8 flex flex-col xs:flex-row sm:flex-row flex-wrap gap-3">
              <Button
                size="lg"
                href={`tel:${STORE_DETAILS.phone.replace(/\s/g, '')}`}
                className="w-full sm:w-auto"
              >
                Call the store
              </Button>
              <Button
                size="lg"
                variant="secondary"
                href="#contact"
                className="w-full sm:w-auto"
              >
                Visit the shop
              </Button>
            </div>

            <dl className="animate-hero-fade-delay-2 mt-8 sm:mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-md border-t border-ink/10 pt-5 sm:pt-6">
              <div>
                <dt className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-muted">
                  Hours
                </dt>
                <dd className="mt-1 text-xs sm:text-sm font-semibold text-ink">6 AM – 9 PM</dd>
              </div>
              <div>
                <dt className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-muted">
                  Open
                </dt>
                <dd className="mt-1 text-xs sm:text-sm font-semibold text-ink">Every day</dd>
              </div>
              <div>
                <dt className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-muted">
                  Delivery
                </dt>
                <dd className="mt-1 text-xs sm:text-sm font-semibold text-ink">On call</dd>
              </div>
            </dl>
          </div>

          {/* Collage — all three store photos */}
          <div className="animate-hero-fade order-1 lg:order-2 relative justify-self-center lg:justify-self-end w-full max-w-[320px] sm:max-w-[400px] lg:max-w-[440px]">
            <div
              className="absolute -inset-2 sm:-inset-3 lg:-inset-4 bg-brand/10 -z-0 translate-x-1.5 sm:translate-x-2 translate-y-1.5 sm:translate-y-2"
              aria-hidden
            />
            <div className="relative grid grid-cols-2 grid-rows-2 gap-1.5 sm:gap-2.5 h-[min(38vh,300px)] sm:h-[min(48vh,420px)] lg:h-[min(58vh,520px)]">
              <div className="row-span-2 overflow-hidden bg-stone-200 ring-1 ring-ink/10">
                <ImageWithFallback
                  src={ASSETS.storeInterior1}
                  alt="Inside Bagbazar Provision Stores — shelves and freezer"
                  fallbackSrc={FALLBACK_IMAGES.store}
                  className="h-full w-full object-cover object-[center_35%]"
                  loading="eager"
                  fetchPriority="high"
                  width={ASSET_DIMENSIONS[ASSETS.storeInterior1]?.[0]}
                  height={ASSET_DIMENSIONS[ASSETS.storeInterior1]?.[1]}
                  sizes="(max-width: 640px) 45vw, 240px"
                />
              </div>
              <div className="overflow-hidden bg-stone-200 ring-1 ring-ink/10">
                <ImageWithFallback
                  src={ASSETS.storeInterior2}
                  alt="Staples on the shelf"
                  fallbackSrc={FALLBACK_IMAGES.aisle}
                  className="h-full w-full object-cover object-[center_28%]"
                  loading="eager"
                  width={ASSET_DIMENSIONS[ASSETS.storeInterior2]?.[0]}
                  height={ASSET_DIMENSIONS[ASSETS.storeInterior2]?.[1]}
                  sizes="(max-width: 640px) 40vw, 200px"
                />
              </div>
              <div className="overflow-hidden bg-stone-200 ring-1 ring-ink/10">
                <ImageWithFallback
                  src={ASSETS.storeInterior3}
                  alt="Store aisle view"
                  fallbackSrc={FALLBACK_IMAGES.store}
                  className="h-full w-full object-cover object-[center_22%]"
                  loading="eager"
                  width={ASSET_DIMENSIONS[ASSETS.storeInterior3]?.[0]}
                  height={ASSET_DIMENSIONS[ASSETS.storeInterior3]?.[1]}
                  sizes="(max-width: 640px) 40vw, 200px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
